import { brl } from "@/lib/api";

export const METHODS = [
  ["dinheiro", "Dinheiro"],
  ["pix", "PIX"],
  ["cartao_debito", "Débito"],
  ["cartao_credito", "Crédito"],
];

export const METHOD_LABEL = Object.fromEntries(METHODS);

/**
 * Cupom de venda para bobina térmica.
 *
 * Sem largura fixa em pixels de propósito: o agente de impressão é quem sabe se
 * a bobina daquele balcão é de 58 ou 80mm, e ele ajusta o papel ao imprimir.
 */
export function buildReceiptHtml(r) {
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  // Abatimento de linha sai NO CUPOM, abaixo do item. Sem isso o papel mostraria
  // um total que não fecha com a soma das linhas, e é o cliente que confere.
  const items = r.items.map((i) => {
    const bruto = i.qty * i.price;
    const desc = Number(i.discount) || 0;

    return `
    <tr><td colspan="2">${esc(i.name)}${i.variant ? ` <span class="v">(${esc(i.variant)})</span>` : ""}</td></tr>
    <tr class="ln"><td>${i.qty} × ${brl(i.price)}</td><td class="r">${brl(bruto)}</td></tr>`
      + (desc > 0
        ? `<tr class="ln"><td class="v">&nbsp;&nbsp;desconto</td><td class="r v">-${brl(desc)}</td></tr>`
        : "");
  }).join("");
  const pays = r.payments.map((p) => `<tr><td>${esc(METHOD_LABEL[p.method] || p.method)}</td><td class="r">${brl(p.amount)}</td></tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    @page{margin:0}
    *{margin:0;padding:0;box-sizing:border-box}
    /* Sem recuo próprio: quem afasta o texto da borda é o agente de impressão,
       que sabe quanto da bobina daquele balcão a cabeça térmica realmente
       alcança (ver profiles.js, margin_mm). Um padding em px aqui somava ao dele
       e estreitava o cupom sem motivo. */
    body{font-family:-apple-system,"Segoe UI",sans-serif;color:#000;font-size:12px;line-height:1.35;margin:0;padding:0}
    /* Nada abaixo de 11px: a 203dpi o traço engorda e o texto miúdo sai borrado
       na bobina — foi por isso que a linha da variação ficou ilegível. */
    .c{text-align:center}.r{text-align:right}.b{font-weight:700}.v{font-size:11px;color:#000}
    h1{font-size:15px}.muted{font-size:11px}
    /* Nome de produto longo não pode empurrar a coluna do valor para fora do
       papel — foi assim que "KIT SABONETE E ALCOOL GEL" comeu o preço. */
    td{overflow-wrap:anywhere}
    .r{white-space:nowrap;padding-left:6px}
    table{width:100%;border-collapse:collapse}.items td{padding:1px 0}
    hr{border:none;border-top:1px dashed #000;margin:6px 0}.tot{font-size:14px}
  </style></head><body>
    <div class="c"><h1 class="b">Ortopédica São Paulo</h1><div class="muted">Cupom de venda (não fiscal)</div></div>
    <hr>
    <div>Venda: <span class="b">${esc(r.number)}</span></div>
    <div class="muted">${new Date().toLocaleString("pt-BR")}</div>
    ${r.customer ? `<div>Cliente: ${esc(r.customer)}</div>` : ""}
    <hr>
    <table class="items">${items}</table>
    <hr>
    <table>
      <tr><td>Subtotal</td><td class="r">${brl(r.subtotal)}</td></tr>
      ${r.discount > 0 ? `<tr><td>Desconto</td><td class="r">-${brl(r.discount)}</td></tr>` : ""}
      ${r.shipping > 0 ? `<tr><td>Frete / entrega</td><td class="r">+${brl(r.shipping)}</td></tr>` : ""}
      <tr class="b tot"><td>TOTAL</td><td class="r">${brl(r.total)}</td></tr>
    </table>
    <hr>
    <table>${pays}
      ${r.received > 0 ? `<tr><td>Recebido</td><td class="r">${brl(r.received)}</td></tr>` : ""}
      ${r.change > 0 ? `<tr class="b"><td>Troco</td><td class="r">${brl(r.change)}</td></tr>` : ""}
    </table>
    ${r.nfce ? `<hr><div class="c muted">NFC-e ${esc(r.nfce)}</div>` : ""}
    <hr>
    <div class="c muted">Obrigado pela preferência!</div>
  </body></html>`;
}

/**
 * Converte uma venda já gravada no formato do cupom.
 *
 * O `buildReceiptHtml` nasceu para o PDV, montando o cupom a partir do carrinho
 * em memória, e por isso fala em `price`/`variant`. Uma venda vinda do servidor
 * fala em `unit_price`/`variant_label` — e sem esta tradução o cupom de uma
 * venda de ontem sairia com item sem nome e total zerado.
 *
 * Vive aqui, ao lado do construtor: mapeamento longe do formato que ele espera é
 * o tipo de coisa que diverge no primeiro campo que alguém renomeia.
 */
export function receiptFromSale(sale) {
  const itens = sale.items || [];
  const pagos = sale.payments || [];

  return {
    number: sale.number,
    customer: sale.customer_name && sale.customer_name !== "Consumidor Final" ? sale.customer_name : null,
    items: itens.map((i) => ({
      name: i.product_name,
      variant: i.variant_label,
      qty: i.qty,
      price: Number(i.unit_price) || 0,
      discount: Number(i.discount) || 0,
    })),
    subtotal: Number(sale.subtotal) || 0,
    discount: Number(sale.discount) || 0,
    shipping: Number(sale.shipping) || 0,
    total: Number(sale.total) || 0,
    payments: pagos.map((p) => ({ method: p.method, amount: Number(p.amount) || 0 })),
    // Troco é do instante do fechamento e não fica gravado na venda. Zero aqui
    // não é "não houve troco": é "não dá para saber", e inventar um valor no
    // papel seria pior que omitir a linha.
    received: 0,
    change: 0,
    // A NFC-e, quando existe, é o que liga este papel ao documento fiscal.
    nfce: (sale.invoices || []).find((i) => i.type === "nfce" && i.status === "autorizada")?.number || null,
  };
}
